import { diffWords } from "diff";

import type { AnalyticsColumn } from "../models/audit/analyticsColumn";
import type { AttributeTestColumn } from "../models/audit/attributeTestColumn";
import { getColumnType } from "../models/audit/attributeTestTranslator";
import type {
  ColumnType,
  EvaluatedColumn,
  ProcedureColumn,
  SupportColumn,
} from "../models/audit/auditSession";

/**
 * Utility for the Column Version History feature.
 *
 * Takes two versions of the same column (a historical snapshot and the current
 * column — both `ProcedureColumn`) and produces a per-field description of what
 * changed.
 */

export type DiffSegmentType = "eq" | "add" | "del";

/** One chunk of text*/
export interface DiffSegment {
  text: string;
  type: DiffSegmentType;
}

export type FieldStatus = "changed" | "unchanged";

export interface FieldDiff {
  key: string;
  label: string;
  status: FieldStatus;
  segments: DiffSegment[];
}

/**
 * Resolves an attribute's id to its display name. The name lives on the
 * procedure, not the column, so the caller (which has the procedure) supplies
 * it. Only attribute-test columns need it.
 */
export type ResolveAttributeName = (attributeId: string) => string;

/** Fields we never diff — machine metadata, not something a user inputs. */
type IgnoredFieldKey = "id" | "createdAt" | "updatedAt";

/**
 * Every field of a column subtype except the ignored metadata. Drives the
 * exhaustiveness guard: each subtype's field map below must cover all of these
 * or fail to compile — so a newly-added field (string OR array) can't silently
 * drop out of the history.
 */
type DiffableKey<T> = Exclude<keyof T, IgnoredFieldKey>;

interface FieldMeta {
  label: string;
  // `true` for free-text fields (word-diffed). `false` for fields swapped
  // wholesale: dropdowns, list fields, and the linked attribute.
  freeText: boolean;
  getFieldText: (
    column: ProcedureColumn,
    resolveAttributeName?: ResolveAttributeName,
  ) => string;
}

const text = (value: string | null | undefined): string => value ?? "";

/**
 * Single source of truth for the diffable fields of each column type. Each map
 * is `Record<DiffableKey<Subtype>, FieldMeta>`, so it is exhaustive over the
 * subtype's fields (minus metadata): a newly-added field fails to compile here
 * until it is added (or excluded). Insertion order is the display order.
 */
const SUPPORT_FIELDS: Record<DiffableKey<SupportColumn>, FieldMeta> = {
  name: {
    label: "Name",
    freeText: true,
    getFieldText: (c) => text(c.supportColumn?.name),
  },
  description: {
    label: "Description",
    freeText: true,
    getFieldText: (c) => text(c.supportColumn?.description),
  },
  dataType: {
    label: "Output Type",
    freeText: false,
    getFieldText: (c) => text(c.supportColumn?.dataType),
  },
  procedureRequiredDocuments: {
    label: "Required Documents",
    freeText: false,
    getFieldText: (c) =>
      (c.supportColumn?.procedureRequiredDocuments ?? [])
        .map((doc) => doc.shortName)
        .join(", "),
  },
};

const EVALUATED_FIELDS: Record<DiffableKey<EvaluatedColumn>, FieldMeta> = {
  name: {
    label: "Name",
    freeText: true,
    getFieldText: (c) => text(c.evaluatedColumn?.name),
  },
  instructions: {
    label: "Instructions",
    freeText: true,
    getFieldText: (c) => text(c.evaluatedColumn?.instructions),
  },
  dataType: {
    label: "Output Type",
    freeText: false,
    getFieldText: (c) => text(c.evaluatedColumn?.dataType),
  },
  dependencies: {
    label: "Dependencies",
    freeText: false,
    getFieldText: (c) =>
      (c.evaluatedColumn?.dependencies ?? [])
        .map((dependency) => dependency.name)
        .join(", "),
  },
};

// `attributeId` is a column-envelope field shared by all column types, so it is
// diffed via ASSIGNED_ATTRIBUTE_FIELD below rather than here — and excluded from
// this map's exhaustiveness guard.
const ATTRIBUTE_TEST_FIELDS: Record<
  Exclude<DiffableKey<AttributeTestColumn>, "attributeId">,
  FieldMeta
> = {
  name: {
    label: "Name",
    freeText: true,
    getFieldText: (c) => text(c.attributeTestColumn?.name),
  },
  passCriteria: {
    label: "Pass Criteria",
    freeText: true,
    getFieldText: (c) => text(c.attributeTestColumn?.passCriteria),
  },
  failCriteria: {
    label: "Fail Criteria",
    freeText: true,
    getFieldText: (c) => text(c.attributeTestColumn?.failCriteria),
  },
  naCriteria: {
    label: "N/A Criteria",
    freeText: true,
    getFieldText: (c) => text(c.attributeTestColumn?.naCriteria),
  },
  procedureGuidance: {
    label: "Procedure Guidance",
    freeText: true,
    getFieldText: (c) => text(c.attributeTestColumn?.procedureGuidance),
  },
  tolerances: {
    label: "Tolerances",
    freeText: true,
    getFieldText: (c) => text(c.attributeTestColumn?.tolerances),
  },
  expectedEvidences: {
    label: "Expected Evidences",
    freeText: true,
    getFieldText: (c) => text(c.attributeTestColumn?.expectedEvidences),
  },
};

/**
 * Analytics columns are authored with the same criteria as attribute tests, so the
 * history reads the same fields off the analytics slot, plus the workflow it
 * delegates the verdict to.
 *
 * `workflowBinding` is excluded: it is a nested object, not text, and it changes
 * only via the attach and configure routes, never via a criteria field, so
 * surfacing it here would flag a change the author never made.
 * `suggestedCategoryId` is excluded for the same reason: only the AI plan sets it.
 */
const ANALYTICS_FIELDS: Record<
  Exclude<
    DiffableKey<AnalyticsColumn>,
    "workflowBinding" | "suggestedCategoryId"
  >,
  FieldMeta
> = {
  name: {
    label: "Name",
    freeText: true,
    getFieldText: (c) => text(c.analyticsColumn?.name),
  },
  passCriteria: {
    label: "Pass Criteria",
    freeText: true,
    getFieldText: (c) => text(c.analyticsColumn?.passCriteria),
  },
  failCriteria: {
    label: "Fail Criteria",
    freeText: true,
    getFieldText: (c) => text(c.analyticsColumn?.failCriteria),
  },
  naCriteria: {
    label: "N/A Criteria",
    freeText: true,
    getFieldText: (c) => text(c.analyticsColumn?.naCriteria),
  },
  procedureGuidance: {
    label: "Procedure Guidance",
    freeText: true,
    getFieldText: (c) => text(c.analyticsColumn?.procedureGuidance),
  },
  tolerances: {
    label: "Tolerances",
    freeText: true,
    getFieldText: (c) => text(c.analyticsColumn?.tolerances),
  },
  expectedEvidences: {
    label: "Expected Evidences",
    freeText: true,
    getFieldText: (c) => text(c.analyticsColumn?.expectedEvidences),
  },
  workflowName: {
    label: "Workflow",
    freeText: false,
    getFieldText: (c) => text(c.analyticsColumn?.workflowName),
  },
  draftedTest: {
    label: "Drafted Test",
    freeText: true,
    getFieldText: (c) => text(c.analyticsColumn?.draftedTest),
  },
};

/**
 * The linked attribute lives on the column envelope (`column.attributeId`), not
 * on a subtype, so it applies to every column type — the editor's "Assigned
 * Attribute" dropdown is shown for support / evaluated / attribute-test alike.
 * Diffed for all types via this shared field.
 */
const ASSIGNED_ATTRIBUTE_FIELD: FieldMeta = {
  label: "Assigned Attribute",
  freeText: false,
  getFieldText: (c, resolveAttributeName) => {
    const id = c.attributeId;
    if (!id) return "";
    // Falls back to the raw id when no resolver is supplied; callers pass
    // resolveAttributeName to show the attribute's name.
    return resolveAttributeName?.(id) ?? id;
  },
};

const FIELDS_BY_TYPE: Record<ColumnType, Record<string, FieldMeta>> = {
  support: SUPPORT_FIELDS,
  evaluated: EVALUATED_FIELDS,
  attribute_test: ATTRIBUTE_TEST_FIELDS,
  analytics: ANALYTICS_FIELDS,
};

function segmentType(part: {
  added?: boolean;
  removed?: boolean;
}): DiffSegmentType {
  if (part.added) return "add";
  if (part.removed) return "del";
  return "eq";
}

/**
 * Free-text → word-level segments via jsdiff. Non-prose → atomic: the whole old
 * value is one `del` segment and the whole new value one `add` segment (or a
 * single `eq` when unchanged), with no word-level matching.
 */
function buildSegments(
  oldValue: string,
  newValue: string,
  freeText: boolean,
): DiffSegment[] {
  if (freeText) {
    return diffWords(oldValue, newValue).map((part) => ({
      text: part.value,
      type: segmentType(part),
    }));
  }

  if (oldValue === newValue) {
    return oldValue === "" ? [] : [{ text: oldValue, type: "eq" }];
  }

  const segments: DiffSegment[] = [];
  if (oldValue !== "") segments.push({ text: oldValue, type: "del" });
  if (newValue !== "") segments.push({ text: newValue, type: "add" });
  return segments;
}

/**
 * Build the per-field diff between two versions of the same column.
 *
 * The field set is resolved from the **current** column's type. If the column
 * has no resolvable type (no subtype object present) returns an empty diff.
 *
 * `hasDocumentCategories` mirrors the editor's gate: the Required Documents
 * picker is only shown when the procedure has document categories, so the diff
 * hides that row too when there are none (it would otherwise always be empty).
 *
 * `hasAssignedAttribute` gates the envelope-level Assigned Attribute row the
 * same way, for callers whose "current" column cannot represent the attribute
 * link — the revise wizard's draft support columns have no attribute field, so
 * diffing it there would flag a phantom change on every version.
 */
export function buildColumnVersionDiff(
  oldColumn: ProcedureColumn,
  newColumn: ProcedureColumn,
  resolveAttributeName?: ResolveAttributeName,
  hasDocumentCategories = true,
  hasAssignedAttribute = true,
): FieldDiff[] {
  const typeKey = getColumnType(newColumn);
  if (typeKey === null) return [];

  // The envelope-level Assigned Attribute is diffed for every column type, then
  // the type-specific fields.
  const fields: [string, FieldMeta][] = [
    ...(hasAssignedAttribute
      ? [["attributeId", ASSIGNED_ATTRIBUTE_FIELD] as [string, FieldMeta]]
      : []),
    ...Object.entries(FIELDS_BY_TYPE[typeKey]),
  ];

  // Drop the Required Documents row when the procedure has no document
  // categories — the editor hides its picker in that case, so showing an
  // always-empty diff row here is just noise.
  const visibleFields = hasDocumentCategories
    ? fields
    : fields.filter(([key]) => key !== "procedureRequiredDocuments");

  return visibleFields.map(([key, meta]) => {
    const oldValue = meta.getFieldText(oldColumn, resolveAttributeName);
    const newValue = meta.getFieldText(newColumn, resolveAttributeName);
    const segments = buildSegments(oldValue, newValue, meta.freeText);
    const hasChange = segments.some((segment) => segment.type !== "eq");
    return {
      key,
      label: meta.label,
      status: hasChange ? "changed" : "unchanged",
      segments,
    };
  });
}
